import { FormOutlined } from "@ant-design/icons";
import { Button, Layout, message, Modal, Pagination } from "antd";
import * as React from "react";
import ReactTable, { Column, RowInfo, SortingRule } from "react-table";
import { TableAPI } from "../API/TableAPI";
import { columnToReactTableColumn, ColumnDescription } from "../Model/Column";
import Entry from "../Model/Entry";
import { CreateCampaignModal } from "./CreateEntryModals/CreateCampaingModal";
import { CreateNetworkModal } from "./CreateEntryModals/CreateNetworkModal";
import { CreateSatelliteModal } from "./CreateEntryModals/CreateSatelliteModal";
import { CreateMarineModal } from "./CreateEntryModals/CreateMarineModal";
import { FilterForm } from "./FilterForm";
import { ShowEntryModal } from "./ShowEntryModal/ShowEntryModal";

import "../../node_modules/react-table/react-table.css";
import "./TableTab.css";
import {
  CreateEntryModalProps,
  EditEntryModalProps,
} from "./CreateEntryModals/CreateEntryModals";
import { CreateModelModal } from "./CreateEntryModals/CreateModelModal";

const { Sider, Content } = Layout;

export interface TableTabProps {
  /** Resource path (/api/{name}) */
  resourcePath: string;
  /**
   * Currently logged in user
   * Used when creating/editing entries
   * * Should be null when logged out.
   */
  user?: {
    userId: number;
    fullName: string;
    isAdmin: boolean;
  };
}

export interface TableTabState {
  /** One page of rows, currently in display! */
  rows: Entry[];
  /** Selected entry, displayed with "ShowEntryModal" */
  selectedEntry?: Entry;
  /** Column descriptions, received from server */
  columns: Record<string, ColumnDescription>;
  /** react-table column descriptions */
  tableColumns: Column[];
  /** Current filter values, represented as an entry */
  filterValues: Entry;
  /** Pagination info */
  pagination: {
    total: number;
    current: number;
    pageSize: number;
  };
  /** Visibility of modals */
  modalVisibility: {
    newEntry: boolean;
    editEntry: boolean;
    details: boolean;
  };
  /** True if currently loading from server, displays a spinner */
  isLoading: boolean;
  /** Sorting rule (which column? asc/desc?) */
  sorting: SortingRule;
}

export class TableTab extends React.Component<TableTabProps, TableTabState> {
  /** Instance of TableAPI for the specific resource this tab displays, use for fetching/creating entries */
  private api: TableAPI;

  constructor(props: TableTabProps) {
    super(props);

    // Create TableAPI
    this.api = new TableAPI(this.props.resourcePath);

    // Default state
    this.state = {
      rows: [],
      selectedEntry: undefined,
      columns: {},
      tableColumns: [],
      filterValues: {},
      pagination: { total: 0, current: 1, pageSize: 20 },
      modalVisibility: { newEntry: false, editEntry: false, details: false },
      isLoading: false,
      sorting: { id: "", desc: false },
    };
  }

  public async componentDidMount() {
    // Create TableAPI instance for given resource
    this.api = new TableAPI(this.props.resourcePath);

    // Fetch column and then rows
    await this.fetchColumns();
    await this.fetchRows();
  }

  public componentDidUpdate(prevProps: TableTabProps) {
    // If the resource path changed clean the table and refresh columns & data
    if (prevProps.resourcePath !== this.props.resourcePath) {
      // * If we don't remove tableColumns we get a race condition
      // * with react-table's cell renderers and usually it crashes.
      this.setState(
        {
          rows: [],
          columns: {},
          tableColumns: [],
          isLoading: true,
          selectedEntry: undefined,
        },
        () => {
          this.componentDidMount();
        }
      );
    }
  }

  public render() {
    return (
      <Layout className="tab-content">
        <Sider className="form-sider" theme="light" width={350}>
          <div className="form-sider-content">
            <div className="form-header">
              <h2>Filters</h2>
              <Button
                type="primary"
                icon={<FormOutlined />}
                onClick={this.showNewEntryModal}
              >
                Contribute
              </Button>
            </div>

            <div className="filter-form">
              <FilterForm
                columns={this.state.columns}
                values={this.state.filterValues}
                onChange={this.onFilterChange}
              />
            </div>
          </div>
        </Sider>
        <Content className="table-container">
          <ReactTable
            className="table -highlight -stripped"
            columns={this.state.tableColumns}
            data={this.state.rows}
            loading={this.state.isLoading}
            sorted={[this.state.sorting]}
            onSortedChange={this.onSortedChange}
            manual={true}
            showPagination={false}
            getTdProps={this.getRowProps}
          />
          <Pagination
            className="table-pagination"
            showSizeChanger={true}
            onChange={this.onChangePage}
            onShowSizeChange={this.onChangePageSize}
            defaultPageSize={20}
            current={this.state.pagination.current}
            pageSize={this.state.pagination.pageSize}
            total={this.state.pagination.total}
          />
        </Content>
        {this.props.user !== undefined && this.getCreateEntryModal()}
        {this.props.user !== undefined &&
          this.state.selectedEntry !== undefined &&
          this.getEditEntryModal(this.state.selectedEntry)}
        <ShowEntryModal
          user={this.props.user}
          entry={this.state.selectedEntry}
          columns={this.state.columns}
          tableAPI={this.api}
          visible={this.state.modalVisibility.details}
          onHide={this.hideShowEntryModal}
          onEdit={this.showEditEntryModal}
        />
      </Layout>
    );
  }

  // === API calls ===

  /** Fetches column descriptions from the server */
  private fetchColumns = async () => {
    const columns = await this.api.getColumns();
    const tableColumns = Object.entries(columns).map(([key, col]) =>
      columnToReactTableColumn(key, col)
    );

    // Set table sorting to the first visible column, ascending
    const firstColumn = tableColumns.filter((col) => col.show)[0];
    const key = firstColumn ? firstColumn.id : "";

    this.setState({
      columns,
      tableColumns,
      sorting: { id: key!!, desc: false },
    });
  };

  /** Fetch rows from server */
  private fetchRows = async (
    count: number = this.state.pagination.pageSize,
    offset: number = (this.state.pagination.current - 1) *
      this.state.pagination.pageSize,
    orderBy: string = ""
  ) => {
    // Show loading spinner
    this.setState({ isLoading: true });

    // Get sorting rule and convert to string (column name prefixed by - if descending)
    const sorting = this.state.sorting;
    orderBy = sorting.desc ? `-${sorting.id}` : sorting.id;

    // Request rows
    const result = await this.api.get(
      count,
      offset,
      orderBy,
      this.makeURLfromFilters()
    );
    if (result === undefined) {
      message.error("Connection error! Could not load entries.");
      this.setState({ isLoading: false });
      return;
    }

    // TODO DataAvailability required special handling here
    const rows = result.results;
    const rowCount = result.rowCount;

    this.setState((prevState: TableTabState) => {
      return {
        rows,
        isLoading: false,
        pagination: {
          ...prevState.pagination,
          total: rowCount,
        },
      };
    });
  };

  // === Modals ===

  private getCreateEntryModal() {
    const props = {
      mode: "create",
      visible: this.state.modalVisibility.newEntry,
      onHide: this.hideNewEntryModal,
    } as CreateEntryModalProps;

    switch (this.props.resourcePath) {
      case "satellites":
        return <CreateSatelliteModal {...props} />;
      case "groundBased":
        return <CreateNetworkModal {...props} />;
      case "marine":
        return <CreateMarineModal {...props} />;
      case "models":
        return <CreateModelModal {...props} />;
      case "campaigns":
        return <CreateCampaignModal {...props} />;
      default:
        console.error(
          `[TableTab.getCreateEntryModal] No create entry modal for resoursePath = ${this.props.resourcePath}`
        );
        return <span />;
    }
  }

  private getEditEntryModal(entry: Entry) {
    const props = {
      mode: "edit",
      entry,
      visible: this.state.modalVisibility.editEntry,
      onHide: this.hideEditEntryModal,
    } as EditEntryModalProps;

    switch (this.props.resourcePath) {
      case "satellites":
        return <CreateSatelliteModal {...props} />;
      case "groundBased":
        return <CreateNetworkModal {...props} />;
      case "marine":
        return <CreateMarineModal {...props} />;
      case "models":
        return <CreateModelModal {...props} />;
      case "campaigns":
        return <CreateCampaignModal {...props} />;
      default:
        console.error(
          `[TableTab.getEditEntryModal] No create entry modal for resoursePath = ${this.props.resourcePath}`
        );
        return <span />;
    }
  }

  /** Displays the 'New Entry' modal */
  private showNewEntryModal = () => {
    if (this.props.user === undefined) {
      // Show small error modal
      Modal.warning({
        title: "Action requires account",
        content: `To contribute you need to create an account and have it
                  approved by one of the administrators. To register, use
                  the form at the top right of the screen.`,
      });

      return;
    }

    this.setState((prevState: TableTabState) => {
      return {
        modalVisibility: {
          ...prevState.modalVisibility,
          newEntry: true,
        },
      };
    });
  };

  /** Hides the 'New Entry' modal */
  private hideNewEntryModal = (refresh = false) => {
    this.setState((prevState: TableTabState) => {
      return {
        modalVisibility: {
          ...prevState.modalVisibility,
          newEntry: false,
        },
      };
    });

    if (refresh) {
      this.fetchRows();
    }
  };

  /** Shows the 'Show Entry' modal */
  private showShowEntryModal = (entryIndex: number) => {
    this.setState((prevState: TableTabState) => {
      return {
        selectedEntry: this.state.rows[entryIndex],
        modalVisibility: {
          ...prevState.modalVisibility,
          details: true,
        },
      };
    });
  };

  /** Hides the 'Show Entry' modal */
  private hideShowEntryModal = (refresh = false) => {
    this.setState((prevState: TableTabState) => {
      return {
        modalVisibility: {
          ...prevState.modalVisibility,
          details: false,
        },
      };
    });

    if (refresh) {
      this.fetchRows();
    }
  };

  /** Shows the 'Edit Entry' modal */
  private showEditEntryModal = () => {
    if (this.props.user === undefined) {
      // Show small error modal
      Modal.warning({
        title: "Action requires account",
        content: `To contribute you need to create an account and have it
                  approved by one of the administrators. To register, use
                  the form at the top right of the screen.`,
      });

      return;
    }

    this.setState((prevState: TableTabState) => {
      return {
        modalVisibility: {
          ...prevState.modalVisibility,
          details: false,
          editEntry: true,
        },
      };
    });
  };

  /** Shows the 'Edit Entry' modal */
  private hideEditEntryModal = (refresh = false) => {
    this.setState((prevState: TableTabState) => {
      return {
        modalVisibility: {
          ...prevState.modalVisibility,
          editEntry: false,
        },
      };
    });

    if (refresh) {
      this.fetchRows();
    }
  };

  // === Table Callbacks ===

  /**
   * Handle sorting changes (user clicks on column heading)
   */
  private onSortedChange = (sorting: SortingRule[]) => {
    this.setState({ sorting: sorting[0] }, () => {
      this.fetchRows();
    });
  };

  private onChangePage = (page: number, pageSize?: number) => {
    const pageSizeValue = pageSize ? pageSize : 20;
    this.setState((prevState: TableTabState) => {
      const pagination = prevState.pagination;
      pagination.current = page;
      pagination.pageSize = pageSizeValue;

      return { pagination };
    });
    this.fetchRows(pageSize, (page - 1) * pageSizeValue);
  };

  private onChangePageSize = (currentSize: number, newSize: number) => {
    this.setState((prevState: TableTabState) => {
      const pagination = prevState.pagination;
      pagination.current = 1;
      pagination.pageSize = newSize;
      return { pagination };
    });
    this.fetchRows(newSize, (this.state.pagination.current - 1) * currentSize);
  };

  private getRowProps = (
    state: TableTabState,
    rowInfo?: RowInfo,
    column?: Column
  ) => {
    // Only add onClick listeners to rows that display entries
    // Empty rows have rowInfo === undefined
    if (rowInfo !== undefined) {
      return {
        onClick: () => this.showShowEntryModal(rowInfo.index),
      };
    }

    return {};
  };

  // === Filtering ===
  private makeURLfromFilters = () => {
    const values = this.state.filterValues;
    let url = "";

    for (const key of Object.keys(values)) {
      const value = values[key];

      if (value !== undefined) {
        // Add values to end of url
        const isArray = Array.isArray(value);

        if (isArray && value.length > 0) {
          url += `&${key}=${value.join("|")}`;
        } else if (!isArray) {
          url += `&${key}=${value}`;
        }
      }
    }

    return url;
  };

  private onFilterChange = (filterValues: Entry) => {
    const pagination = { ...this.state.pagination, current: 1 };
    this.setState({ filterValues, pagination }, () => {
      this.fetchRows();
    });
  };
}
